import React, { useState } from 'react';
import { DateRangePicker } from 'rsuite';
import { PiMicrosoftExcelLogoFill } from 'react-icons/pi';
import { Button, Modal } from '@nsi-advisory-services/components';
import { useTimesheetReportExport } from '../../hooks/useTimesheet';
import { showToast } from '../../utils/toast';
import { formatDate, isoFromDate } from '../../utils/timesheet';
import { EXPORT_FORMATS, buildEntries, buildSummary, writeExport } from '../../utils/timesheetReportExport';

/*
  Timesheet Reports > Export: the summary or every entry, in a format, for
  the dates and as deep as asked, with a running account of what the file will
  hold. The other filters come from the page's filter bar as they stand.

  Summary depth: one row per project, or a row per task under each project,
  or (when the page is grouped some other way) the grouping on screen.
  Entries depth: whether each entry carries its task as well as its project.
*/

const SUMMARY_DEPTHS = {
  projects: { label: 'Projects', groupBy: 'project', thenBy: null },
  tasks: { label: 'Projects and tasks', groupBy: 'project', thenBy: 'task' },
};

const ENTRIES_DEPTHS = {
  projects: { label: 'Project only' },
  tasks: { label: 'Project and task' },
};

const KINDS = [
  { key: 'summary', label: 'Summary', icon: 'bi-table' },
  { key: 'entries', label: 'Entries', icon: 'bi-list-ul' },
];

const ENTRY_LIMIT = 20000;
// Past this many rows a PDF is a phone book; the dialog says so.
const PDF_COMFORT = 2000;

const sameRange = (a, b) => Boolean(a?.[0] && b?.[0]) && isoFromDate(a[0]) === isoFromDate(b[0]) && isoFromDate(a[1]) === isoFromDate(b[1]);

export default function ReportExportModal({
  open,
  onClose,
  range: pageRange,
  quickRanges,
  params,
  groupBy,
  thenBy,
  groupLabels,
  filters,
  withMoney,
  entryCount,
}) {
  const onScreen = !(groupBy === 'project' && (!thenBy || thenBy === 'task'));
  const summaryDepths = onScreen
    ? { ...SUMMARY_DEPTHS, screen: { label: 'As grouped on screen', groupBy, thenBy } }
    : SUMMARY_DEPTHS;
  // Where each kind's detail starts: the summary as the page is grouped, entries with their task.
  const startingDepth = (k) => {
    if (k !== 'summary') return 'tasks';
    if (onScreen) return 'screen';
    return thenBy === 'task' ? 'tasks' : 'projects';
  };

  const [kind, setKind] = useState('summary');
  const [format, setFormat] = useState('xlsx');
  const [range, setRange] = useState(pageRange);
  const [depth, setDepth] = useState(() => startingDepth('summary'));
  const isSummary = kind === 'summary';
  const depths = isSummary ? summaryDepths : ENTRIES_DEPTHS;
  const chooseKind = (k) => {
    setKind(k);
    setDepth(startingDepth(k));
  };
  const exportMutation = useTimesheetReportExport();

  const start = range?.[0] ? isoFromDate(range[0]) : null;
  const end = range?.[1] ? isoFromDate(range[1]) : null;
  const grouping = isSummary ? depths[depth] : null;
  const knownCount = sameRange(range, pageRange) ? entryCount : null;

  const shape = (() => {
    if (!isSummary) {
      return `One row per time entry: date, employee, client, project${depth === 'tasks' ? ', task' : ''}, description, start and end, hours, billable, R&D credit and source.`;
    }
    if (depth === 'projects') return 'One row per project.';
    if (depth === 'tasks') return 'A row per project, with a row under it for each of its tasks.';
    return `Grouped by ${groupLabels[groupBy].toLowerCase()}${thenBy ? `, then by ${groupLabels[thenBy].toLowerCase()}` : ''}, as on screen.`;
  })();
  const figures = isSummary
    ? `Hours, billable hours and %, R&D hours, entries and people${withMoney ? ', plus cost, projected revenue and margin' : ''}, with a total row.`
    : withMoney ? 'Each with its pay rate, cost, ProPlan rate and projected revenue.' : null;

  const run = async () => {
    try {
      const data = await exportMutation.mutateAsync({
        ...params,
        start,
        end,
        ...(isSummary ? { groupBy: grouping.groupBy, thenBy: grouping.thenBy || undefined } : {}),
        includeEntries: !isSummary,
      });
      const built = isSummary
        ? buildSummary({ report: data, groupBy: grouping.groupBy, thenBy: grouping.thenBy, groupLabels })
        : buildEntries({ data, depth });
      const title = isSummary ? 'Timesheet summary' : 'Timesheet entries';
      await writeExport({
        format,
        filename: `timesheet-${isSummary ? 'summary' : 'entries'}-${data.start}-to-${data.end}`,
        title,
        about: [
          ['Report', title],
          ['Dates', `${formatDate(data.start, { month: 'short', day: 'numeric', year: 'numeric' })} – ${formatDate(data.end, { month: 'short', day: 'numeric', year: 'numeric' })}`],
          ['Detail', depths[depth].label],
          ...filters,
          ['Exported', new Date().toLocaleString('en-US')],
        ],
        ...built,
      });
      if (data.truncated) showToast('warning', `The export was capped at ${ENTRY_LIMIT.toLocaleString('en-US')} entries. Narrow the range to get the rest.`);
      onClose();
    } catch (err) {
      showToast('error', String(err?.message || err));
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="md">
      <Modal.Header>
        <Modal.Title>Export</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ts-export">
          <div className="bdc-segment bdc-segment--block" role="radiogroup" aria-label="What to export">
            {KINDS.map((k) => (
              <button
                key={k.key}
                type="button"
                role="radio"
                aria-checked={kind === k.key}
                className={`bdc-btn${kind === k.key ? ' bdc-btn--primary' : ''}`}
                onClick={() => chooseKind(k.key)}
              >
                <i className={`bi ${k.icon}`} aria-hidden="true" />
                {k.label}
              </button>
            ))}
          </div>

          <div className="ts-export__field">
            <span className="ts-export__label" id="ts-export-format">Format</span>
            <div className="ts-export__formats" role="radiogroup" aria-labelledby="ts-export-format">
              {EXPORT_FORMATS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="radio"
                  aria-checked={format === f.key}
                  className={`ts-export__format${format === f.key ? ' is-on' : ''}`}
                  onClick={() => setFormat(f.key)}
                >
                  {f.key === 'xlsx'
                    ? <PiMicrosoftExcelLogoFill className="ts-export__excel" aria-hidden="true" />
                    : <i className={`bi ${f.icon}`} aria-hidden="true" />}
                  <span className="ts-export__format-name">{f.label}</span>
                  <span className="ts-export__format-help">{f.help}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="ts-export__row">
            <label className="ts-export__field">
              <span className="ts-export__label">Dates</span>
              <DateRangePicker
                value={range}
                onChange={(value) => setRange(value || pageRange)}
                format="MM/dd/yyyy"
                character=" – "
                isoWeek={false}
                weekStart={0}
                ranges={quickRanges}
                cleanable={false}
                placement="autoVerticalStart"
                block
              />
            </label>
            <div className="ts-export__field">
              <span className="ts-export__label" id="ts-export-depth">Detail</span>
              <div className="bdc-segment" role="radiogroup" aria-labelledby="ts-export-depth">
                {Object.entries(depths).map(([key, d]) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={depth === key}
                    className={`bdc-btn bdc-btn--sm${depth === key ? ' bdc-btn--primary' : ''}`}
                    onClick={() => setDepth(key)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <section className="ts-export__about" aria-label="What will be exported">
            <p>{shape}</p>
            {figures ? <p>{figures}</p> : null}
            {!isSummary ? (
              <p>
                {knownCount != null
                  ? `${knownCount.toLocaleString('en-US')} ${knownCount === 1 ? 'entry' : 'entries'} in this range.`
                  : 'Entries are counted when you export.'}
                {` Up to ${ENTRY_LIMIT.toLocaleString('en-US')}.`}
              </p>
            ) : null}
            <dl className="ts-export__filters">
              {filters.map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </React.Fragment>
              ))}
            </dl>
            <p className="ts-export__note">These follow the filter bar; change them there.</p>
            {format === 'pdf' && !isSummary && (knownCount == null || knownCount > PDF_COMFORT) ? (
              <p className="ts-export__warn">
                <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                A PDF of many entries runs to a lot of pages. Excel or CSV suit a long list better.
              </p>
            ) : null}
          </section>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" icon="upload" onClick={run} loading={exportMutation.isPending} disabled={!start || !end}>
          Export
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
